<script setup lang="ts">
import { dateToRelativeTimeHumanized } from '@/composables/useUtils'
import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import { useI18n } from 'vue-i18n'
import {
  BSVariantEnum,
  GLOBAL_MODALS_SECTION_KEY,
  NOTIFICATION_PREFIXES,
  NOTIFICATION_TO_CREATE_ASSESSMENT,
  SaiftyNotification,
} from '@/config'
import useDownloadDocuments from '@/composables/useDownloadDocuments'
import Loading from '@/components/loading/Loading.vue'
import { isSyncNotification } from '@/composables/useSyncConfiguration'
import AddSyncListToSkipListModal from '@/components/modals/AddSyncListToSkipListModal.vue'
import HandleCertificateRejectModal from '@/components/modals/HandleCertificateRejectModal.vue'
import { computed, defineAsyncComponent, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { CERTIFICATE_URL_ID, CERTIFICATE_PUSHED_TYPE, CertificateRejectionType } from '@/composables/useDataSpace'
import { CERTIFICATE_MAPPING_ID } from '@/composables/useMappings'
import { CertificatePushStatusPayloadCertificateStatusEnum, CertificateRequestStatus } from '@coac-gmbh/saifty-ccm-api'

interface Props {
  data: SaiftyNotification
  isInsideModal?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isInsideModal: false,
})

const emits = defineEmits<{
  (event: 'redirectToSomePage'): void
}>()

const CERTIFICATE_REQUEST_NOTIFICATION_TYPE = 'DS_CERTIFICATE_REQUEST'
const CERTIFICATE_PUSH_NOTIFICATION_TYPE = 'DS_CERTIFICATE_PUSH'
const CERTIFICATE_AVAILABLE_NOTIFICATION_TYPE = 'DS_CERTIFICATE_AVAILABLE'
const CERTIFICATE_STATUS_NOTIFICATION_TYPE = 'DS_CERTIFICATE_STATUS'

const { t } = useI18n()
const { downloadingSource, downloadCVSFromFilestorageAsXLSX, downloadFileFromFilestorage } = useDownloadDocuments()
const HandleCertificateRequestModal = defineAsyncComponent(
  () => import('@/components/modals/HandleCertificateRequestModal.vue'),
)
const addSyncListToSkipListModal = ref<InstanceType<typeof AddSyncListToSkipListModal>>()
const handleCertificateRequestModal = ref<InstanceType<typeof HandleCertificateRequestModal>>()
const handleCertificateRejectModal = ref<InstanceType<typeof HandleCertificateRejectModal>>()
const notificationSelected = ref<SaiftyNotification>()
const certificateToRejectData = ref<{ certificate: SaiftyNotification; type: CertificateRejectionType }>()

const shouldOpenCertificateRequestModal = ref(false)
const state = reactive({ mounted: false })

const notificationsTypeWithSuccessVariant = [CERTIFICATE_AVAILABLE_NOTIFICATION_TYPE, NOTIFICATION_PREFIXES.New]
const notificationsTypeWithWarningVariant = [NOTIFICATION_PREFIXES.Update]
const notificationsTypeWithDangerVariant = [NOTIFICATION_PREFIXES.Remove]

const isDataObjectNotification = computed(() => {
  return props.data.data.source === 'dataobject'
})

const isApprovalNotification = computed(() => {
  return props.data.data.source === 'approval' || props.data.data.source === undefined
})

onMounted(() => {
  /* State mounted is necessary in pages that use teleport */
  state.mounted = true
})

watch(handleCertificateRequestModal, (modalInstance) => {
  if (!shouldOpenCertificateRequestModal.value || !modalInstance) return
  modalInstance.openModal()
  shouldOpenCertificateRequestModal.value = false
})

function notificationIsFromToday(date: string | number) {
  dayjs.extend(isToday)
  return dayjs(date).isToday()
}

function getNotificationBSVariant(notificationType: string) {
  if (notificationsTypeWithDangerVariant.some((type) => notificationType.includes(type))) return BSVariantEnum.Danger
  if (notificationsTypeWithWarningVariant.some((type) => notificationType.includes(type))) return BSVariantEnum.Warning
  if (notificationsTypeWithSuccessVariant.some((type) => notificationType.includes(type))) return BSVariantEnum.Success
  else return BSVariantEnum.Primary
}

function formatDate(date: string | number) {
  return dayjs.unix(parseInt(date.toString())).format(`MMM D [${t('at')}] h:mm a`)
}

function getVariantColorClass(variant: string) {
  if (variant === BSVariantEnum.Primary) {
    return 'primary-800'
  } else if (variant === BSVariantEnum.Danger) {
    return 'danger-800'
  } else {
    return variant
  }
}

function getNotificationNameTranslation(saiftyNotification: SaiftyNotification) {
  switch (saiftyNotification.messageType) {
    case 'DUPLICATE_FILE_UPLOAD':
      return t('Duplicate file uploaded')
    case 'NEW_RECORD_IN_APPROVAL':
      return t('Approval created')
    case 'EXTRACTION_COMPLETED':
      return t('Extraction completed')
    case 'SAP_SYNC':
      return t('Syncronization completed')
    case 'SAP_SUB_UPDATE':
      return t('SAP Substance updated')
    case 'SAP_SYNC_LOADING':
      return t('Synchronization is loading') + '...'
    case 'APPROVAL_DOCUMENT_APPROVED':
      return t('Approval document approved')
    case 'DATA_ASSET_UPDATED_FROM_SAP':
      return t('Data asset has been updated from SAP data')
    case 'SOURCE_DATA_OBJECT_UPDATE':
      return 'sourceDataObjectName' in saiftyNotification.data && saiftyNotification.data.sourceDataObjectName
        ? t(`New version for '{documentName}' available`, {
            documentName: saiftyNotification.data.sourceDataObjectName,
          })
        : t('An Original Shared Data Object has been updated')
    case 'DATA_ASSET_UPDATED':
      return t('A Data asset has been updated from SAP data')
    case 'DATA_OBJECT_UPDATED':
      return t('A Data object has been updated from SAP data')
    case 'SAP_SUB_CREATE':
      return t('SAP substance created')
    case 'SAP_SUB_ITEM_DELETE':
      return t('SAP substance item deleted')
    case NOTIFICATION_TO_CREATE_ASSESSMENT:
      return t('Assessment created')
    case 'DATA_ASSET_LOADED':
      return t('Load to Lake Complete')
    case 'LOCAL_COPY_DATA_OBJECT_AUTO_UPDATE':
      return t('An assessment object has been automatically updated.')
    case 'SAP_AUTO_UPDATE':
      return t('Automatic SAP synchronization')
    case 'FED_ASSESSMENT_LOADING':
      return t('Generating Federated Assessment')
    case CERTIFICATE_REQUEST_NOTIFICATION_TYPE:
      return `Certificate Request received for ${saiftyNotification.data?.content?.certificateType || '--'} from ${saiftyNotification.data?.header?.senderBpn || '--'}`
    case CERTIFICATE_PUSH_NOTIFICATION_TYPE:
      return `Certificate ${saiftyNotification.data?.content?.namePlate?.productNameAtManufacturer || ''} has been pushed from ${saiftyNotification.data?.header?.senderBpn || '--'}`
    case CERTIFICATE_AVAILABLE_NOTIFICATION_TYPE:
      return `Certificate ${saiftyNotification.data?.content?.certificateType || '--'} from ${saiftyNotification.data?.header?.senderBpn || '--'} is available`
    case CERTIFICATE_STATUS_NOTIFICATION_TYPE:
      return 'Certificate Status changed'
    default:
      return saiftyNotification.messageType
  }
}

function getDefaultMessage(notificationName: string) {
  switch (notificationName) {
    case 'SOURCE_DATA_OBJECT_UPDATE':
      return t('Click to view all related local copies')
    case CERTIFICATE_REQUEST_NOTIFICATION_TYPE:
      return t('New document received by provider')
    default:
      return ''
  }
}

async function handleGoToCertificateRequest() {
  notificationSelected.value = props.data
  shouldOpenCertificateRequestModal.value = true
  await nextTick()
  if (handleCertificateRequestModal.value) {
    handleCertificateRequestModal.value.openModal()
    shouldOpenCertificateRequestModal.value = false
  }
}

async function handleRejectCertificate(type: CertificateRejectionType) {
  certificateToRejectData.value = {
    certificate: props.data,
    type,
  }
  await nextTick()
  handleCertificateRejectModal.value?.openModal()
}

function goToExternalLink(link: string) {
  window.open(link, '_blank')
}
</script>

<template>
  <div :class="{ 'my-3': !props.isInsideModal }">
    <div
      :class="[
        `d-flex flex-fill bg-white border-4 border-start border-${getVariantColorClass(
          getNotificationBSVariant(props.data.messageType),
        )} py-3 px-2 `,
        { 'rounded-1': !props.isInsideModal },
      ]"
    >
      <i-ic-outline-info
        v-if="getNotificationBSVariant(props.data.messageType) === BSVariantEnum.Primary"
        class="d-flex flex-shrink-0 fs-5 ms-1 me-2"
        :class="`text-${getVariantColorClass(getNotificationBSVariant(props.data.messageType))}`"
      />

      <i-mdi-checkbox-marked-circle-outline
        v-if="getNotificationBSVariant(props.data.messageType) === BSVariantEnum.Success"
        class="d-flex flex-shrink-0 fs-5 ms-1 me-2"
        :class="`text-${getVariantColorClass(getNotificationBSVariant(props.data.messageType))}`"
      />

      <i-gg-close-o
        v-if="getNotificationBSVariant(props.data.messageType) === BSVariantEnum.Danger"
        class="d-flex flex-shrink-0 fs-5 ms-1 me-2"
        :class="`text-${getVariantColorClass(getNotificationBSVariant(props.data.messageType))}`"
      />

      <i-ion-warning-outline
        v-if="getNotificationBSVariant(props.data.messageType) === BSVariantEnum.Warning"
        class="d-flex flex-shrink-0 fs-5 ms-1 me-2"
        :class="`text-${getVariantColorClass(getNotificationBSVariant(props.data.messageType))}`"
      />

      <div class="d-flex flex-fill justify-content-between ms-1 me-3">
        <div>
          <h5 class="text-dark mb-1">
            {{ getNotificationNameTranslation(props.data) }}
          </h5>
          <div v-if="props.data.data.message" class="text-primary-800 text-break">{{ props.data.data.message }}</div>
          <div v-else class="text-primary-800 text-break">
            {{ getDefaultMessage(props.data.messageType) }}
          </div>

          <small class="text-primary-800 mt-1">
            {{
              notificationIsFromToday(props.data.timestamp)
                ? dateToRelativeTimeHumanized(props.data.timestamp, true)
                : formatDate(props.data.timestamp)
            }}
          </small>
        </div>
        <div class="d-flex flex-column flex-shrink-0">
          <button
            v-if="isSyncNotification(props.data.data)"
            class="btn btn-sm btn-outline-primary rounded-pill py-1 ms-2 px-3 d-flex gap-2 align-items-center mb-1 w-100 justify-content-center"
            @click="addSyncListToSkipListModal?.openModal()"
          >
            {{ t('Add list to Skiplist') }}
          </button>

          <template v-if="props.data.messageType === NOTIFICATION_TO_CREATE_ASSESSMENT">
            <router-link
              v-if="!props.data.data.dataObjectId && (props.data.data.dataAssetId || props.data.data.dataAssetsId)"
              :to="{
                name: 'assessment',
                query: {
                  instanceId: props.data.data.instance,
                  assessmentId: props.data.data.dataAssetId || props.data.data.dataAssetsId,
                },
              }"
              class="btn btn-sm btn-primary rounded-pill py-1 ms-2 px-3"
              @click="emits('redirectToSomePage')"
            >
              {{ t('Go to assessment') }}
            </router-link>
          </template>
          <template v-else>
            <router-link
              v-if="!props.data.data.dataObjectId && (props.data.data.dataAssetId || props.data.data.dataAssetsId)"
              :to="{
                name: 'data-assets-dataAssetId',
                params: {
                  dataAssetId: props.data.data.dataAssetId || props.data.data.dataAssetsId,
                },
              }"
              class="btn btn-sm btn-primary rounded-pill py-1 ms-2 px-3"
              @click="emits('redirectToSomePage')"
            >
              {{ t('Go to asset') }}
            </router-link>
          </template>

          <button
            v-if="props.data.data.fileId"
            class="btn btn-sm btn-primary rounded-pill py-1 m-1 ms-2 px-3 d-flex gap-2 align-items-center mb-1 w-100 justify-content-center text-nowrap"
            :disabled="downloadingSource"
            @click="downloadCVSFromFilestorageAsXLSX(props.data.data.fileId)"
          >
            {{ t('Download file') }}
            <Loading v-if="downloadingSource" size="sm" extra-classes="mt-1" />
          </button>

          <button
            v-else-if="props.data.data.DOC_file_id"
            class="btn btn-sm btn-primary rounded-pill py-1 m-1 ms-2 px-3 d-flex gap-2 align-items-center mb-1 w-100 justify-content-center text-nowrap"
            :disabled="downloadingSource"
            @click="downloadFileFromFilestorage(props.data.data.DOC_file_id)"
          >
            {{ t('Download file') }}
            <Loading v-if="downloadingSource" size="sm" extra-classes="mt-1" />
          </button>

          <div v-else-if="props.data.messageType === CERTIFICATE_REQUEST_NOTIFICATION_TYPE" class="d-flex flex-column">
            <button
              class="btn btn-sm btn-primary rounded-pill py-1 m-1 ms-2 px-3 d-flex gap-2 align-items-center mb-1 w-100 justify-content-center text-nowrap"
              @click="handleGoToCertificateRequest()"
            >
              {{ t('Go to request') }}
            </button>

            <button
              v-if="
                !props.data.data?.certificateStatus ||
                props.data.data?.certificateStatus === CertificateRequestStatus.InProgress
              "
              class="btn btn-sm btn-secondary rounded-pill py-1 m-1 ms-2 px-3 d-flex gap-2 align-items-center mb-1 w-100 justify-content-center text-nowrap"
              @click="handleRejectCertificate(CertificateRejectionType.PULL)"
            >
              {{ t('Reject request') }}
            </button>
          </div>

          <div v-if="props.data.messageType === CERTIFICATE_PUSH_NOTIFICATION_TYPE" class="d-flex flex-column">
            <router-link
              v-if="
                props.data.data?.id && props.data.data?.header?.senderBpn && props.data.data?.header?.senderFeedbackUrl
              "
              :to="{
                name: 'passport-shared-urlSuffix',
                params: {
                  urlSuffix: CERTIFICATE_URL_ID,
                },
                query: {
                  BPN: props.data.data?.header?.senderBpn,
                  counterPartyAddress: props.data.data?.header?.senderFeedbackUrl,
                  mappingId: CERTIFICATE_MAPPING_ID,
                  certificateId: props.data.data?.id,
                  certificateType: CERTIFICATE_PUSHED_TYPE,
                },
              }"
              class="btn btn-sm btn-primary rounded-pill py-1 ms-2 px-3 w-100"
              @click="emits('redirectToSomePage')"
            >
              {{ t('Open certificate') }}
            </router-link>

            <button
              v-if="
                !props.data.data?.certificateStatus ||
                props.data.data?.certificateStatus === CertificatePushStatusPayloadCertificateStatusEnum.Received
              "
              class="btn btn-sm btn-secondary rounded-pill py-1 m-1 ms-2 px-3 d-flex gap-2 align-items-center mb-1 w-100 justify-content-center text-nowrap"
              @click="handleRejectCertificate(CertificateRejectionType.PUSH)"
            >
              {{ t('Reject certificate') }}
            </button>
          </div>

          <template
            v-if="props.data.data.dataObjectId && (props.data.data.dataAssetId || props.data.data.dataAssetsId)"
          >
            <router-link
              v-if="isDataObjectNotification"
              :to="{
                name: 'data-assets-dataAssetId-dataObjectId',
                params: {
                  dataAssetId: props.data.data.dataAssetId || props.data.data.dataAssetsId,
                  dataObjectId: props.data.data.dataObjectId,
                },
              }"
              class="btn btn-outline-primary rounded-pill py-1 ms-2 w-100"
              @click="emits('redirectToSomePage')"
            >
              {{ t('Go to Data Object') }}
            </router-link>

            <router-link
              v-else-if="isApprovalNotification"
              :to="{
                name: 'approvals-id',
                params: {
                  id: props.data.data.dataObjectId,
                },
                query: {
                  dataObjectId: props.data.data.dataObjectId,
                  dataAssetId: props.data.data.dataAssetId || props.data.data.dataAssetsId,
                },
              }"
              class="btn btn-outline-success rounded-pill py-1 ms-2 w-100"
              @click="emits('redirectToSomePage')"
            >
              {{ t('Go to Approval') }}
            </router-link>
          </template>

          <router-link
            v-if="props.data.data.localDataAssetId && props.data.data.localDataObjectId"
            :to="{
              name: 'data-assets-dataAssetId-dataObjectId',
              params: {
                dataAssetId: props.data.data.localDataAssetId,
                dataObjectId: props.data.data.localDataObjectId,
              },
            }"
            class="btn btn-outline-success rounded-pill py-1 ms-2 w-100"
            @click="emits('redirectToSomePage')"
          >
            {{ t('View local copy') }}
          </router-link>

          <router-link
            v-else-if="
              props.data.data.sourceDataAssetId &&
              props.data.data.sourceDataObjectId &&
              props.data.data.sourceInstanceId
            "
            :to="{
              name: 'data-objects-shared',
              query: {
                sourceDataObjectId: props.data.data.sourceDataObjectId,
                sourceDataAssetId: props.data.data.sourceDataAssetId,
                sourceInstanceId: props.data.data.sourceInstanceId,
              },
            }"
            class="btn btn-outline-success rounded-pill py-1 ms-2 w-100"
            @click="emits('redirectToSomePage')"
          >
            {{ t('View shared object') }}
          </router-link>

          <button
            v-else-if="props.data.data.link"
            class="btn btn-sm btn-primary rounded-pill py-1 m-1 ms-2 px-3 d-flex gap-2 align-items-center mb-1 w-100 justify-content-center text-nowrap"
            @click="goToExternalLink(props.data.data.link)"
          >
            {{ props.data.data.button_title || t('Open') }}
          </button>
        </div>
      </div>
    </div>

    <hr v-if="props.isInsideModal" class="text-secondary my-2" :style="{ height: '2px' }" />
  </div>

  <Teleport v-if="state.mounted" :to="`#${GLOBAL_MODALS_SECTION_KEY}`" :disabled="!props.isInsideModal">
    <AddSyncListToSkipListModal
      v-if="isSyncNotification(props.data.data)"
      ref="addSyncListToSkipListModal"
      :data="props.data"
    />

    <HandleCertificateRequestModal
      v-if="notificationSelected"
      ref="handleCertificateRequestModal"
      :certificate-data="notificationSelected.data"
      @certificate-proccesed="() => (notificationSelected = undefined)"
    />

    <HandleCertificateRejectModal
      v-if="certificateToRejectData"
      ref="handleCertificateRejectModal"
      :certificate-rejection-type="certificateToRejectData.type"
      :certificate-data="certificateToRejectData.certificate.data"
      @certificate-proccesed="() => (certificateToRejectData = undefined)"
    />
  </Teleport>
</template>
